import { useState, type ReactNode } from "react";
import {
  Calculator,
  Layers3,
  Percent,
  PiggyBank,
  Scale,
  WalletCards,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { SectionHeading } from "../components/ui";
import { useCountry } from "../context/CountryContext";
import {
  bankrollStake,
  combinedOdds,
  decimalProfit,
  decimalReturn,
  impliedProbability,
  unitsFromStake,
} from "../lib/calculators";

function ToolCard({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="surface rounded-2xl p-5 sm:p-6">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-lime/10 text-lime">
          {icon}
        </span>
        <h2 className="display text-xl font-black">{title}</h2>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}
function Input({
  label,
  value,
  onChange,
  step = "0.01",
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  step?: string;
}) {
  return (
    <label className="block text-xs font-bold uppercase tracking-wider text-muted">
      {label}
      <input
        type="number"
        min="0"
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="mt-2 w-full rounded-xl border border-soft bg-white/[.04] px-4 py-3 text-lg font-bold text-white outline-none focus:border-lime/50"
      />
    </label>
  );
}
function Result({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="mt-4 rounded-xl bg-lime p-4 text-[#07110d]">
      <div className="text-xs font-bold uppercase tracking-wider">{label}</div>
      <div className="display mt-1 text-2xl font-black">{value}</div>
      {sub && <div className="mt-1 text-xs font-semibold">{sub}</div>}
    </div>
  );
}

export function ToolsPage() {
  const { country } = useCountry();
  const symbol = country.currencySymbol;
  const [stake, setStake] = useState(100);
  const [odds, setOdds] = useState(2.1);
  const [legs, setLegs] = useState([1.5, 1.8, 1.35]);
  const [bankroll, setBankroll] = useState(1000);
  const [unitPercent, setUnitPercent] = useState(2);
  const [unitValue, setUnitValue] = useState(20);
  const total = combinedOdds(legs);
  return (
    <div className="page-shell py-10 sm:py-14">
      <SEO
        title="Free betting calculators"
        description="Calculate odds, accumulators, implied probability, payout, bankroll stake and betting units in your browser."
        path="/tools"
      />
      <SectionHeading
        headingLevel={1}
        eyebrow="No signup required"
        title="Betting calculators"
        description="All calculations happen in your browser. Outputs are mathematical illustrations, not recommendations."
      />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <ToolCard icon={<Calculator />} title="Odds Calculator">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Stake" value={stake} onChange={setStake} />
            <Input label="Decimal odds" value={odds} onChange={setOdds} />
          </div>
          <Result
            label="Potential return"
            value={`${symbol} ${decimalReturn(stake, odds).toFixed(2)}`}
            sub={`Potential profit: ${symbol} ${decimalProfit(stake, odds).toFixed(2)}`}
          />
        </ToolCard>
        <ToolCard icon={<Layers3 />} title="Accumulator Calculator">
          <div className="space-y-2">
            {legs.map((leg, index) => (
              <div className="flex gap-2" key={index}>
                <Input
                  label={`Selection ${index + 1}`}
                  value={leg}
                  onChange={(value) =>
                    setLegs((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? value : item,
                      ),
                    )
                  }
                />
                <button
                  aria-label={`Remove selection ${index + 1}`}
                  className="mt-6 rounded-xl border border-soft px-3 text-muted"
                  onClick={() =>
                    setLegs((current) =>
                      current.filter((_, itemIndex) => itemIndex !== index),
                    )
                  }
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <button
            className="mt-3 text-sm font-bold text-lime"
            onClick={() => setLegs((current) => [...current, 1.5])}
          >
            + Add selection
          </button>
          <Result
            label="Combined odds"
            value={total.toFixed(2)}
            sub={`${symbol} ${stake.toFixed(2)} returns ${symbol} ${decimalReturn(stake, total).toFixed(2)} hypothetically`}
          />
        </ToolCard>
        <ToolCard icon={<Percent />} title="Implied Probability">
          <Input label="Decimal odds" value={odds} onChange={setOdds} />
          <Result
            label="Implied probability"
            value={`${impliedProbability(odds).toFixed(1)}%`}
            sub="This includes the bookmaker margin when taken from quoted odds."
          />
        </ToolCard>
        <ToolCard icon={<WalletCards />} title="Payout Calculator">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Stake" value={stake} onChange={setStake} />
            <Input label="Odds" value={odds} onChange={setOdds} />
          </div>
          <Result
            label="Payout"
            value={`${symbol} ${decimalReturn(stake, odds).toFixed(2)}`}
            sub={`Stake ${symbol} ${stake.toFixed(2)} + profit ${symbol} ${decimalProfit(stake, odds).toFixed(2)}`}
          />
        </ToolCard>
        <ToolCard icon={<PiggyBank />} title="Bankroll Calculator">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Bankroll" value={bankroll} onChange={setBankroll} />
            <Input
              label="Unit size %"
              value={unitPercent}
              onChange={setUnitPercent}
            />
          </div>
          <Result
            label="One unit"
            value={`${symbol} ${bankrollStake(bankroll, unitPercent).toFixed(2)}`}
            sub="A budget aid only. Never chase losses or bet money needed for essentials."
          />
        </ToolCard>
        <ToolCard icon={<Scale />} title="Unit Calculator">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Stake" value={stake} onChange={setStake} />
            <Input
              label="Unit value"
              value={unitValue}
              onChange={setUnitValue}
            />
          </div>
          <Result
            label="Stake in units"
            value={`${unitsFromStake(stake, unitValue).toFixed(2)} units`}
          />
        </ToolCard>
      </div>
    </div>
  );
}
